<template>
  <ContentWrap>
    <el-alert
      type="info"
      :closable="false"
      show-icon
      title="本所工时：须指定案件成员律师；可提交/审批。机构账号代录时请选律师。"
      class="mb-12px"
    />
    <el-form :inline="true" :model="query" class="mb-12px" @submit.prevent>
      <el-form-item label="案件ID">
        <el-input-number v-model="query.caseId" :min="1" controls-position="right" class="!w-120px" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable class="!w-120px">
          <el-option label="草稿" value="DRAFT" />
          <el-option label="待审" value="PENDING" />
          <el-option label="已确认" value="APPROVED" />
          <el-option label="作废" value="VOID" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">搜索</el-button>
        <el-button type="success" @click="openCreate">登记工时</el-button>
      </el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="id" label="编号" width="70" />
      <el-table-column prop="firmCaseNo" label="案号" min-width="120" />
      <el-table-column prop="caseTitle" label="案件" min-width="140" show-overflow-tooltip />
      <el-table-column prop="lawyerName" label="律师" width="100" />
      <el-table-column prop="workDate" label="日期" width="110" />
      <el-table-column prop="minutes" label="分钟" width="80" />
      <el-table-column prop="workType" label="类型" width="100" />
      <el-table-column prop="status" label="状态" width="90" />
      <el-table-column prop="content" label="内容" min-width="140" show-overflow-tooltip />
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <el-button v-if="row.status === 'DRAFT'" link type="primary" @click="doSubmit(row)">提交</el-button>
          <el-button v-if="row.status === 'PENDING'" link type="success" @click="doApprove(row, true)">通过</el-button>
          <el-button v-if="row.status === 'PENDING'" link type="danger" @click="doApprove(row, false)">驳回</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="getList"
    />

    <Dialog v-model="createVisible" title="登记工时" width="520px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="案件" prop="caseId">
          <el-select v-model="form.caseId" filterable class="!w-100%" placeholder="本所 Matter">
            <el-option
              v-for="m in matterOptions"
              :key="m.id"
              :label="`${m.firmCaseNo || m.id} · ${m.title}`"
              :value="m.id!"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="律师" prop="lawyerId">
          <el-select v-model="form.lawyerId" filterable class="!w-100%" placeholder="须为案件成员">
            <el-option
              v-for="l in lawyerOptions"
              :key="l.id"
              :label="`${l.name || '律师'}（#${l.id}）`"
              :value="l.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="日期" prop="workDate">
          <el-date-picker v-model="form.workDate" type="date" value-format="YYYY-MM-DD" class="!w-100%" />
        </el-form-item>
        <el-form-item label="分钟" prop="minutes">
          <el-input-number v-model="form.minutes" :min="1" :max="1440" />
        </el-form-item>
        <el-form-item label="类型" prop="workType">
          <el-input v-model="form.workType" placeholder="如 CONSULT / DRAFT / COURT" />
        </el-form-item>
        <el-form-item label="内容" prop="content">
          <el-input v-model="form.content" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitCreate">保存草稿</el-button>
      </template>
    </Dialog>
  </ContentWrap>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import {
  FirmPracticeApi,
  type PracticeMatterVO,
  type PracticeTimeVO
} from '@/api/firm/practice'
import { FirmPortalApi, type FirmLawyerVO } from '@/api/firm/portal'

defineOptions({ name: 'FirmPracticeTime' })

const message = useMessage()
const loading = ref(false)
const list = ref<PracticeTimeVO[]>([])
const total = ref(0)
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  caseId: undefined as number | undefined,
  status: undefined as string | undefined
})

const matterOptions = ref<PracticeMatterVO[]>([])
const lawyerOptions = ref<FirmLawyerVO[]>([])
const createVisible = ref(false)
const saving = ref(false)
const formRef = ref<FormInstance>()
const form = reactive({
  caseId: undefined as number | undefined,
  lawyerId: undefined as number | undefined,
  workDate: '',
  minutes: 60,
  workType: 'CONSULT',
  content: ''
})
const rules: FormRules = {
  caseId: [{ required: true, message: '请选择案件', trigger: 'change' }],
  lawyerId: [{ required: true, message: '请选择律师', trigger: 'change' }],
  workDate: [{ required: true, message: '请选择日期', trigger: 'change' }],
  minutes: [{ required: true, message: '请填写分钟', trigger: 'blur' }],
  workType: [{ required: true, message: '请填写类型', trigger: 'blur' }],
  content: [{ required: true, message: '请填写内容', trigger: 'blur' }]
}

const getList = async () => {
  loading.value = true
  try {
    const data = await FirmPracticeApi.timePage(query)
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  query.pageNo = 1
  getList()
}

const openCreate = async () => {
  form.caseId = undefined
  form.lawyerId = undefined
  form.workDate = ''
  form.minutes = 60
  form.workType = 'CONSULT'
  form.content = ''
  createVisible.value = true
  const [matters, lawyers] = await Promise.all([
    FirmPracticeApi.matterPage({ pageNo: 1, pageSize: 100 }),
    FirmPortalApi.lawyerAssignableList()
  ])
  matterOptions.value = matters?.list || []
  lawyerOptions.value = lawyers || []
}

const submitCreate = async () => {
  await formRef.value?.validate()
  if (!form.caseId || !form.lawyerId) return
  saving.value = true
  try {
    await FirmPracticeApi.timeCreate({
      caseId: form.caseId,
      lawyerId: form.lawyerId,
      workDate: form.workDate,
      minutes: form.minutes,
      workType: form.workType.trim(),
      content: form.content.trim()
    })
    message.success('已保存草稿')
    createVisible.value = false
    await getList()
  } finally {
    saving.value = false
  }
}

const doSubmit = async (row: PracticeTimeVO) => {
  if (!row.id) return
  await FirmPracticeApi.timeSubmit(row.id)
  message.success('已提交')
  await getList()
}

const doApprove = async (row: PracticeTimeVO, passed: boolean) => {
  if (!row.id) return
  let rejectReason: string | undefined
  if (!passed) {
    rejectReason = window.prompt('驳回原因') || undefined
    if (!rejectReason) return
  }
  await FirmPracticeApi.timeApprove({ id: row.id, passed, rejectReason })
  message.success(passed ? '已通过' : '已驳回')
  await getList()
}

onMounted(getList)
</script>
